import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["animation", "classes", "className", "max", "overrides", "value"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
var useStyles = createUseStyles(styles, 'Progress');
var Progress = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var animation = _ref.animation,
    classesProp = _ref.classes,
    classNameProp = _ref.className,
    max = _ref.max,
    overridesProp = _ref.overrides,
    value = _ref.value,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    percentage = _useState2[0],
    setPercentage = _useState2[1];
  var oldPercentage = useRef(0);
  var classes = useClasses(useStyles, classesProp);
  var override = getOverrides(overridesProp, Progress.overrides);
  useEffect(function () {
    var newPercentage = value / max * 100;
    if (newPercentage !== percentage) {
      oldPercentage.current = percentage;
      setPercentage(newPercentage);
    }
  }, [value, max, percentage]);
  var containerProps = useMemo(function () {
    var _classNames;
    return _objectSpread(_objectSpread({
      className: classNames(classes.root, classNameProp, (_classNames = {}, _defineProperty(_classNames, classes.low, percentage < 25), _defineProperty(_classNames, classes.medium, percentage >= 25), _defineProperty(_classNames, classes.high, percentage >= 50), _defineProperty(_classNames, classes.higher, percentage >= 75), _defineProperty(_classNames, classes.full, percentage >= 100), _defineProperty(_classNames, classes.animated, !!animation), _classNames)),
      'aria-valuenow': value,
      'aria-valuemin': 0,
      'aria-valuemax': max,
      ref: ref,
      role: 'progressbar'
    }, props), override.root);
  }, [animation, classNameProp, classes.animated, classes.full, classes.high, classes.higher, classes.low, classes.medium, classes.root, max, override.root, percentage, props, ref, value]);
  var progressProps = useMemo(function () {
    var width = "".concat(percentage, "%");
    var style = !!animation ? {
      width: width,
      '--progress-percentage-from': "".concat(oldPercentage.current, "%"),
      '--progress-percentage-to': "".concat(percentage, "%"),
      '--progress-duration': animation.animationDuration || '1s',
      '--progress-delay': animation.animationDelay || 0,
      '--progress-timing-function': animation.animationTimingFunction || 'ease'
    } : {
      width: width
    };
    return {
      className: classes.progressBar,
      style: style
    };
  }, [animation, percentage, classes.progressBar]);
  return /*#__PURE__*/React.createElement("div", containerProps, /*#__PURE__*/React.createElement("span", progressProps));
});
Progress.defaultProps = {
  value: 0,
  max: 100
};
process.env.NODE_ENV !== "production" ? Progress.propTypes = {
  className: PropTypes.string,
  value: PropTypes.number.isRequired,
  max: PropTypes.number,
  overrides: PropTypes.object,
  animation: PropTypes.shape({
    /** Any valid time string that specifies the duration of the animation */
    animationDuration: PropTypes.string,
    /** Any valid time string that specifies the delay of the animation */
    animationDelay: PropTypes.string,
    /** Any valid string that specifies the speed curve of the animation */
    animationTimingFunction: PropTypes.string
  })
} : void 0;
export default /*#__PURE__*/memo(Progress);